import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  GitCompareArrows,
  PackageOpen,
  Percent,
  ShoppingCart,
} from 'lucide-react';

import { KpiCard } from '@/components/shared/KpiCard';

import type { UsageDetailDrilldown } from '../hooks/useUsageMonitoring';
import type { UsageKpisResponse } from '../types';
import {
  biasDirectionKey,
  biasVariant,
  formatCount,
  formatRatioPct,
  formatSignedRatioPct,
} from '../utils/format';

interface Props {
  kpis: UsageKpisResponse | null | undefined;
  loading?: boolean;
  onDrilldown?: (opts: UsageDetailDrilldown) => void;
}

function biasValueClass(ratio: number | null | undefined): string {
  return biasVariant(ratio) === 'warning' ? 'text-orange-600' : 'text-gray-800';
}

export function UsageKpiCards({ kpis, loading, onDrilldown }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'es';

  const adoption = kpis?.adoption.orders_via_stockerai;
  const complete = kpis?.adoption.orders_complete;
  const addedOutside = kpis?.product_presence.added_outside;
  const adherence = kpis?.quantity.adherence;
  const totalBias = kpis?.quantity.total_bias;
  const totalBiasValue = kpis?.quantity.total_bias_value;
  const userBias = kpis?.quantity.user_bias;
  const direction = biasDirectionKey(totalBias?.ratio);
  const biasHelp =
    direction === 'up'
      ? t('adoption.kpis.totalBiasHelpUp')
      : direction === 'down'
        ? t('adoption.kpis.totalBiasHelpDown')
        : t('adoption.kpis.totalBiasHelpNeutral');

  const biasSubtitleParts: string[] = [];
  if (direction === 'up') biasSubtitleParts.push(t('adoption.kpis.riskOverstock'));
  else if (direction === 'down') biasSubtitleParts.push(t('adoption.kpis.riskStockout'));
  else biasSubtitleParts.push(t('adoption.kpis.riskNeutral'));

  if (totalBiasValue && totalBiasValue.products_total > 0) {
    biasSubtitleParts.push(
      t('adoption.kpis.totalBiasCostCoverage', {
        withCost: formatCount(totalBiasValue.products_with_cost, locale),
        total: formatCount(totalBiasValue.products_total, locale),
      }),
    );
  }

  const userDir = kpis?.quantity.user_direction;

  return (
    <section className="space-y-2">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
        <KpiCard
          title={t('adoption.kpis.adoption')}
          value={formatRatioPct(adoption?.ratio, locale)}
          loading={loading}
          icon={<ShoppingCart className="h-5 w-5" />}
          helpText={t('adoption.kpis.adoptionHelp')}
          onClick={
            onDrilldown
              ? () => onDrilldown({ status: '', presence: '', replaceScope: false })
              : undefined
          }
          subtitle={
            adoption
              ? t('adoption.kpis.fraction', {
                  value: formatCount(adoption.value, locale),
                  denominator: formatCount(adoption.denominator, locale),
                })
              : undefined
          }
        />
        <KpiCard
          title={t('adoption.kpis.adherence')}
          value={formatRatioPct(adherence?.ratio, locale)}
          loading={loading}
          icon={<Percent className="h-5 w-5" />}
          helpText={t('adoption.kpis.adherenceHelp')}
          onClick={
            onDrilldown
              ? () =>
                  onDrilldown({
                    status: '',
                    presence: 'EN_AMBOS',
                    replaceScope: false,
                  })
              : undefined
          }
          subtitle={
            adherence
              ? t('adoption.kpis.fraction', {
                  value: formatCount(adherence.value, locale),
                  denominator: formatCount(adherence.denominator, locale),
                })
              : undefined
          }
          nested={{
            title: t('adoption.kpis.userBias'),
            value: formatSignedRatioPct(userBias?.ratio, locale),
            helpText: t('adoption.kpis.userBiasHelp'),
            valueClassName: biasValueClass(userBias?.ratio),
            subtitle: userDir
              ? t('adoption.kpis.userDirection', {
                  equal: formatCount(userDir.equal, locale),
                  up: formatCount(userDir.increased, locale),
                  down: formatCount(userDir.decreased, locale),
                })
              : undefined,
          }}
        />
        <KpiCard
          title={t('adoption.kpis.complete')}
          value={formatRatioPct(complete?.ratio, locale)}
          loading={loading}
          icon={<CheckCircle2 className="h-5 w-5" />}
          helpText={t('adoption.kpis.completeHelp')}
          onClick={
            onDrilldown
              ? () =>
                  onDrilldown({
                    status: 'VIA_STOCKERAI_COMPLETO',
                    presence: '',
                    replaceScope: false,
                  })
              : undefined
          }
          subtitle={
            complete
              ? t('adoption.kpis.fraction', {
                  value: formatCount(complete.value, locale),
                  denominator: formatCount(complete.denominator, locale),
                })
              : undefined
          }
        />
        <KpiCard
          title={t('adoption.kpis.addedOutside')}
          value={formatRatioPct(addedOutside?.ratio, locale)}
          loading={loading}
          icon={<PackageOpen className="h-5 w-5" />}
          helpText={t('adoption.kpis.addedOutsideHelp')}
          variant={
            addedOutside?.ratio != null && addedOutside.ratio > 0.05 ? 'warning' : 'default'
          }
          onClick={
            onDrilldown
              ? () =>
                  onDrilldown({
                    status: 'VIA_STOCKERAI_INCOMPLETO',
                    presence: 'SOLO_ERP',
                    replaceScope: false,
                  })
              : undefined
          }
          subtitle={
            addedOutside
              ? t('adoption.kpis.fraction', {
                  value: formatCount(addedOutside.value, locale),
                  denominator: formatCount(addedOutside.denominator, locale),
                })
              : undefined
          }
        />
        <KpiCard
          title={t('adoption.kpis.totalBias')}
          value={formatSignedRatioPct(totalBias?.ratio, locale)}
          loading={loading}
          icon={<GitCompareArrows className="h-5 w-5" />}
          helpText={biasHelp}
          variant={biasVariant(totalBias?.ratio)}
          secondaryValue={totalBiasValue?.amount ?? null}
          secondaryIsEuro
          secondaryClassName={`text-lg font-semibold tabular-nums ${biasValueClass(totalBias?.ratio)}`}
          valueNote={
            totalBiasValue?.amount != null
              ? t('adoption.kpis.totalBiasValueNote')
              : t('adoption.kpis.totalBiasValueMissing')
          }
          subtitle={biasSubtitleParts.join(' · ')}
          onClick={
            onDrilldown
              ? () =>
                  onDrilldown({
                    status: '',
                    presence: 'EN_AMBOS',
                    replaceScope: false,
                  })
              : undefined
          }
        />
      </div>
    </section>
  );
}
